<script>
  import { onMount } from 'svelte';
  import {
    loadRuntimeConfig,
    createUserManager,
    isAuthCallback,
    clearAuthCallbackFromUrl,
    hasRequiredRole,
  } from './lib/auth.js';
  import { fetchEvents } from './lib/api.js';
  import EventsGrid from './EventsGrid.svelte';

  let config = null;
  let userManager = null;
  let user = null;
  let events = [];
  let loading = true;
  let error = '';
  let forbidden = false;

  onMount(async () => {
    try {
      config = await loadRuntimeConfig();
      userManager = createUserManager(config);

      if (isAuthCallback()) {
        user = await userManager.signinRedirectCallback();
        clearAuthCallbackFromUrl();
      } else {
        user = await userManager.getUser();
      }

      if (user && !user.expired) {
        forbidden = !hasRequiredRole(user, config.oidc.requiredRoles);
        if (!forbidden) {
          await loadEvents();
        }
        loading = false;
      } else {
        // No login button/prompt - go straight to CloudIDP instead of waiting for a click.
        await redirectToLogin();
      }
    } catch (e) {
      error = e.message ?? String(e);
      loading = false;
    }
  });

  async function redirectToLogin() {
    error = '';
    loading = false; // let the "Redirecting to sign in…" message show while signinRedirect() navigates away
    try {
      await userManager.signinRedirect(); // navigates the browser away on success
    } catch (e) {
      error = e.message ?? String(e);
    }
  }

  async function logout() {
    await userManager.removeUser();
    user = null;
    events = [];
    forbidden = false;
    await redirectToLogin();
  }

  async function loadEvents() {
    loading = true;
    error = '';
    try {
      const data = await fetchEvents(config, user.access_token);
      events = data.events ?? [];
    } catch (e) {
      error = e.message ?? String(e);
    } finally {
      loading = false;
    }
  }
</script>

<header class="app-header g-global-top-navigation">
  <div class="app-header-left">
    <div class="app-logo">
      <img src="./logo_petrol_32.svg" alt="WLTP" />
    </div>
    <h1 class="app-title g-brand-logo-application">AWS Health Events</h1>
  </div>
  {#if user && !user.expired}
    <div class="app-header-user">
      <span class="muted" style="color: var(--groupui-sys-color-text-weak-inverse);">{user.profile?.name ?? user.profile?.email ?? 'Signed in'}</span>
      <button class="g-btn g-btn-secondary g-btn-small g-btn-inverted" on:click={logout}>Sign out</button>
    </div>
  {/if}
</header>

<main>
  {#if error}
    <p class="error">{error}</p>
  {/if}

  {#if loading}
    <p class="center muted">Loading…</p>
  {:else if !user || user.expired}
    {#if error}
      <div class="center">
        <button class="g-btn g-btn-secondary g-btn-small" on:click={redirectToLogin}>Try again</button>
      </div>
    {:else}
      <p class="center muted">Redirecting to sign in…</p>
    {/if}
   {:else if forbidden}
    <div class="center">
      <p class="error">
        You do not have the required permissions to access this application. Please contact
        <a href="mailto:wltp-it@volkswagen.de">wltp-it@volkswagen.de</a> to request access.
      </p>
      <button class="g-btn g-btn-secondary g-btn-small" on:click={logout}>Sign out</button>
    </div>
  {:else}
    <EventsGrid {events} onRefresh={loadEvents} />
  {/if}
</main>

<footer class="footer">
  <div class="footer-links">
    <a href="/legal/imprint/">Imprint</a>
    <span class="footer-divider">|</span>
    <a href="/legal/data-protection/">Data Protection Declaration</a>
    <span class="footer-divider">|</span>
    <a href="/legal/conditions-of-use/">Terms of Use</a>
    <span class="footer-divider">|</span>
    <a href="/legal/cookie-policy/">Cookie Policy</a>
    <span class="footer-divider">|</span>
    <a href="/legal/third-party-licenses/">Third Party Licence Notes</a>
  </div>
  <div class="footer-logo">
    <img src="https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoidm9sa3N3YWdlblwvZmlsZVwvY01yZkNhTFlHQVJXOFpzYXJIOTkucG5nIn0:volkswagen:QS2NBlN_tbCagjOiaMX68JspydAkvRjMpAksMHUPYvI" alt="Volkswagen Group" />
  </div>
</footer>
